/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { BaseService } from 'apps/console-core/src/Services/_base';
import { HttpResponse } from 'apps/console-core/src/Services/http';
import { Observable, of, switchMap } from 'rxjs';
import { EntityAnyInsightsConfig, EntityConfig } from './Entities/entities';
import { InsightsServiceConfiguration } from './Insights.service.config';
import { InsightsCreateResponse, InsightsMessageResponse } from './Insights.service.defs';
import {
  InsightsServiceCreateInsightDto,
  InsightsServiceDeleteInsightDto,
  InsightsServiceGetInsightDto,
  InsightsServiceSetConfigDto,
  InsightsServiceUpdateInsightDto,
} from './Insights.service.dto';

/**
 * The insights service.
 * @export
 * @class InsightsService
 */
export class InsightsService extends BaseService {
  /**
   * Creates an instance of InsightsService.
   * @param {InsightsServiceConfiguration} configuration The configuration options to accept.
   * @memberof InsightsService
   */
  public constructor(configuration: InsightsServiceConfiguration) {
    super(configuration);
  }

  /**
   * Creates base api url for the Insights service.
   * @returns {string | null} The base api url for the Insights service.
   * @memberof InsightsService
   */
  getBaseServiceUrl(): string | null {
    // This service is deployed in multiple regions and
    // needs to use the tenant's region.
    return this.getServiceUrlByTenant(`insights`);
  }

  /**
   * Creates api url to get an individual insight.
   * @param {string} insightId The target insight ID.
   * @returns {string | null} The api url for an individual insight. Returns null if the base url is null.
   * @memberof InsightsService
   */
  createInsightUrl(insightId: string): string | null {
    const baseUrl = this.getBaseServiceUrl();

    if (!baseUrl) {
      return null;
    }

    return `${baseUrl}/insights/${insightId}`;
  }

  /**
   * Creates api url to get a list of insights.
   * @returns {string | null} The api url for a list of insights. Returns null if the base url is null.
   * @memberof InsightsService
   */
  createInsightListUrl(): string | null {
    const baseUrl = this.getBaseServiceUrl();

    if (!baseUrl) {
      return null;
    }

    return `${baseUrl}/insights`;
  }

  /**
   * Creates api url to get insight configuration.
   * @returns {string | null} The api url for insight configuration. Returns null if the base url is null.
   * @memberof InsightsService
   */
  createInsightConfigUrl(): string | null {
    const baseUrl = this.getBaseServiceUrl();

    if (!baseUrl) {
      return null;
    }

    return `${baseUrl}/config`;
  }

  /**
   * Attempt to get insight details.
   * @param {InsightsServiceGetInsightDto} dto The insight details to fetch.
   * @returns {Observable<EntityAnyInsightsConfig | null>} The observable of insight details.
   * @memberof InsightsService
   */
  getInsight(dto: InsightsServiceGetInsightDto): Observable<EntityAnyInsightsConfig | null> {
    const { insightId } = dto;
    const requestUrl = this.createInsightUrl(insightId);

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityAnyInsightsConfig> => {
          return this._handleResponseError<EntityAnyInsightsConfig>(value);
        }),
      );
  }

  /**
   * Attempt to get a list of organisation insights.
   * @returns {Observable<Record<string, EntityAnyInsightsConfig>>} The observable of organisation insights.
   * @memberof InsightsService
   */
  getInsights(): Observable<Record<string, EntityAnyInsightsConfig> | null> {
    const requestUrl = this.createInsightListUrl();

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<Record<string, EntityAnyInsightsConfig>> => {
          return this._handleResponseError<Record<string, EntityAnyInsightsConfig>>(value);
        }),
      );
  }

  /**
   * Create insight.
   * @param {InsightsServiceCreateInsightDto} dto The insight details to create.
   * @returns {Observable<InsightsCreateResponse>} The observable of insight details.
   * @memberof InsightsService
   */
  createInsight(dto: InsightsServiceCreateInsightDto): Observable<InsightsCreateResponse | null> {
    const { insight } = dto;
    const requestUrl = this.createInsightListUrl();

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .post<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
        body: insight,
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<InsightsCreateResponse> => {
          return this._handleResponseError<InsightsCreateResponse>(value);
        }),
      );
  }

  /**
   * Update insight.
   * @param {InsightsServiceUpdateInsightDto} dto The insight details to update.
   * @returns {Observable<InsightsMessageResponse>} The observable of insight details.
   * @memberof InsightsService
   */
  updateInsight(dto: InsightsServiceUpdateInsightDto): Observable<InsightsMessageResponse | null> {
    const { insight, insightId } = dto;
    const requestUrl = this.createInsightUrl(insightId);

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .patch<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
        body: insight,
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<InsightsMessageResponse> => {
          return this._handleResponseError<InsightsMessageResponse>(value);
        }),
      );
  }

  /**
   * Delete insight.
   * @param {InsightsServiceDeleteInsightDto} dto The insight details to delete.
   * @returns {Observable<InsightsMessageResponse>} The observable of insight details.
   * @memberof InsightsService
   */
  deleteInsight(dto: InsightsServiceDeleteInsightDto): Observable<InsightsMessageResponse | null> {
    const { insightId } = dto;
    const requestUrl = this.createInsightUrl(insightId);

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .delete<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<InsightsMessageResponse> => {
          return this._handleResponseError<InsightsMessageResponse>(value);
        }),
      );
  }

  /**
   * Get config.
   * @returns {Observable<EntityAnyInsightsConfig>} The observable of insights service configuration.
   * @memberof InsightsService
   */
  getConfig(): Observable<EntityConfig | null> {
    const requestUrl = this.createInsightConfigUrl();

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityConfig> => {
          return this._handleResponseError<EntityConfig>(value);
        }),
      );
  }

  /**
   * Set config.
   * @param {InsightsServiceSetConfigDto} dto The organisation config details to set for.
   * @returns {Observable<EntityAnyInsightsConfigConfig>} The observable of insights service configuration.
   * @memberof InsightsService
   */
  setConfig(dto: InsightsServiceSetConfigDto): Observable<Partial<EntityConfig | null>> {
    const { config } = dto;
    const requestUrl = this.createInsightConfigUrl();

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .put<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
        body: config,
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityConfig> => {
          return this._handleResponseError<EntityConfig>(value);
        }),
      );
  }
}
